import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Paper, Typography} from "@mui/material";
import TryAgain from "../../../../../components/app-specific/try-again";
import useWindowSize from "../../../../../hooks/use-window/size";
import Api from "../../../../../../core/services/api/api";
import Iframe from "react-iframe";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ElementIds from "../../../../../../core/models/element-ids";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const ReportsView = () => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState();
    const [size, setSize] = useState({height: 0, width: 0});
    const topSection = useRef();
    const windowSize = useWindowSize();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('reports-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const selectedReportInfo = useMemo(() => reports?.find(e => e.id === selectedReportId), [reports, selectedReportId]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.reports);
        configureBehavioralTool((tags) => tags.reports);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the list of available reports for the operator to choose from.
     * @type {(function(): Promise<void>)}
     */
    const getOperatorsReports = useCallback(async () => {
        setLoading(true);
        let reports = [];
        const privateReports = await Api.getAvailableDashboards(true);
        const publicReports = await Api.getPublicReports();
        if (!isMounted())
            return;
        if (privateReports?.resultFlag) {
            reports.push(...privateReports?.data ?? []);
        }
        if (privateReports?.resultFlag) {
            reports.push(...publicReports?.data?.map(e => ({
                ...e,
                title: e.title?.concat(' (Public)')
            })) ?? []);
        }
        reports = [...(new Set(reports?.map(e => e.id) ?? []).values())].map(e => reports.find(r => r.id === e))
        setReports(reports);
        setSelectedReportId(reports[0] ? reports[0]?.id : null)
        setLoading(false);
    }, [isMounted]);

    /**
     * Loads the list available reports for the operator to choose from.
     */
    useEffect(() => {
        getOperatorsReports().then();
    }, [getOperatorsReports]);

    /**
     * Listens for the changes of the window size and for each change, calculates the height and width required for
     * the iframe section.
     */
    useEffect(() => {
        if (!topSection?.current)
            return;
        const mainSection = document.getElementById(ElementIds.panel.main).getBoundingClientRect();
        const panelAppbar = document.getElementById(ElementIds.panel.appBar);
        mainSection.height -= panelAppbar.getBoundingClientRect().height;
        const dashboardTopSection = topSection.current.getBoundingClientRect();
        setSize(prevState => {
            const newHeight = mainSection.height - dashboardTopSection.height - 48;
            const newWidth = mainSection.width;
            if (newHeight === prevState.height && newWidth === prevState.width)
                return prevState;
            return {height: newHeight, width: newWidth};
        })
    }, [windowSize, topSection]);

    /**
     * Changes the selected report of this view.
     * @type {(function(*=, *=): void)}
     */
    const onNewReportSelected = useCallback((_, newReportId) => {
        if (newReportId === selectedReportId || !newReportId)
            return
        setSelectedReportId(newReportId);
    }, [selectedReportId]);

    return (
        <>
            <div ref={topSection} className={'w-100 pt-2 d-flex flex-column w-100'}>
                <Typography variant={'h6'} fontWeight={600} marginBlockEnd={2}>
                    Classic Reports
                </Typography>
                {
                    reports?.length > 0 &&
                    <Tabs className={'mb-3'} value={selectedReportId ?? false} onChange={onNewReportSelected}>
                        {
                            reports?.map(e => (
                                <Tab key={e.id} value={e.id} label={e.title}/>
                            ))
                        }
                    </Tabs>
                }
            </div>
            <Paper className='paper p-2' style={{height: size.height}}>
                {
                    loading ?
                        (
                            <div className={'loading chat'}>
                                <div/>
                            </div>
                        ) :
                        (
                            selectedReportId
                                ? <Iframe
                                    title={selectedReportInfo?.title}
                                    allowFullScreen
                                    className={'rounded-xl'}
                                    frameBorder={0}
                                    url={selectedReportInfo?.url}
                                    width='100%'
                                    height='100%'
                                    style={{display: 'block'}}
                                />
                                : <TryAgain
                                    text={'You have not yet selected any reports. Please select one, or contact your admin to assign reports to you.'}
                                    buttonText={'Reload My Reports'}
                                    onClick={getOperatorsReports}
                                />
                        )
                }

            </Paper>
        </>
    );
}

export default ReportsView;
